// Single source of truth for all SEO head tags.
// Canonical domain is fixed and WITHOUT www, matching the server 301/308 redirect target.
export const SITE_URL = "https://ilcassettodellegiraffe.it";

const DEFAULT_TITLE = "Il Cassetto delle Giraffe — di Fabio Campana";
const DEFAULT_DESC =
  "Storie, idee e curiosità per guardare il mondo con occhi diversi.";
const DEFAULT_IMAGE =
  "https://customer-assets-jt897jd0.emergentagent.net/job_campana-thoughts/artifacts/wpo22urf_ChatGPT%20Image%2025%20lug%202026%2C%2009_44_00.png";

const upsertMeta = (attr, key, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const setRobots = (noindex) => {
  let el = document.head.querySelector('meta[name="robots"]');
  if (noindex) {
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
  } else if (el) {
    el.parentNode.removeChild(el);
  }
};

const setJsonLd = (obj) => {
  let script = document.getElementById("seo-jsonld");
  if (!obj) {
    if (script) script.parentNode.removeChild(script);
    return;
  }
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "seo-jsonld";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(obj);
};

// Turn a relative /api/... path into an absolute URL on the canonical domain.
export const absoluteUrl = (pathOrUrl) => {
  if (!pathOrUrl) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE_URL + (pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`);
};

// Deterministically set ALL SEO tags from the CURRENT route/data.
// Called on every route change and on data load — always overwrites, never leaves stale tags.
export const applySeo = ({
  path,
  title,
  description,
  image,
  type = "website",
  noindex = false,
  jsonLd = null,
} = {}) => {
  const url = SITE_URL + (path || "/");
  const t = title || DEFAULT_TITLE;
  const d = description || DEFAULT_DESC;
  const img = absoluteUrl(image);

  document.title = t;
  setCanonical(url);
  upsertMeta("name", "description", d);
  upsertMeta("property", "og:type", type);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:title", t);
  upsertMeta("property", "og:description", d);
  upsertMeta("property", "og:image", img);
  upsertMeta("name", "twitter:title", t);
  upsertMeta("name", "twitter:description", d);
  upsertMeta("name", "twitter:image", img);
  setRobots(noindex);
  setJsonLd(jsonLd);
};

// SEO for a non-existent / deleted article. Google-supported way to keep a
// soft-404 (HTTP 200) URL out of the index within a static SPA:
//   - strong noindex,nofollow
//   - NO canonical (a not-found page must not declare one)
//   - NO Article/BlogPosting structured data
export const applyNotFoundSeo = (path) => {
  const url = SITE_URL + (path || "/");
  document.title = "Pagina non trovata — Il Cassetto delle Giraffe";
  const link = document.head.querySelector('link[rel="canonical"]');
  if (link) link.parentNode.removeChild(link);
  upsertMeta("name", "description", "La pagina richiesta non esiste o è stata rimossa.");
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:title", "Pagina non trovata");
  setRobots(true);
  setJsonLd(null);
};
